import {
    Button,
    Divider,
    IconButton,
    Stack,
    Typography,
    useTheme,
    Dialog,
    DialogTitle,
    DialogContent
} from "@mui/material";
import React, {useEffect, useState} from "react";
import EntityTable, {EntityTableHeaderProps} from "../../../components/list/EntityList";
import {BoatInRace} from "../../../schemas/boats/boatInRaceSchema";
import {useRace} from "../../../utils/races/RaceContext";
import useSWR from "swr";
import {deleteFetcher, getFetcher, postFetcher, putFetcher} from "../../../utils/api/fetchers";
import Loading from "../../../components/status/Loading";
import {Boat, PaginatedBoats} from "../../../schemas/boats/boatsSchema";
import {useCategories} from "../../../utils/categories/CategoriesContext";
import EditIcon from "@mui/icons-material/Edit";
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import buildUrl from "../../../utils/api/buildUrl";
import useTableParams from "../../../components/list/useTableParams";
import {useConfirm} from "material-ui-confirm";
import {useNotification} from "../../../utils/hooks/useNotification";
import BoatRegisterDialog from "../../../components/dialog/BoatRegisterDialog";
import {BoatRegistrationType} from "../../../schemas/races/boatRegistrationSchema";
import BoatCreateDialog from "../../../components/dialog/BoatCreateDialog";
import useMediaQuery from "@mui/material/useMediaQuery";
import {FinishLineRecord} from "../../../schemas/boats/boatFinishLineRecordSchema";
import {getCategoryColor} from "../../../utils/categories/categoryColors";

const BoatRegistration: React.FC = () => {
    const confirm = useConfirm();
    const {showNotification} = useNotification();
    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down("lg"));
//-------------------------------------------------------------------------------------------------------------------------------
    const [selectedId, setSelectedId] = useState<number | undefined>(undefined);
    const [defaultRegisterDialogValue, setDefaultRegisterDialogValue] = useState<number | null | undefined>(undefined);
    const [registerDialogOpen, setRegisterDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [createBoatDialogOpen, setCreateBoatDialogOpen] = useState(false);
    const [unregisteredBoatDialogOpen, setUnregisteredBoatDialogOpen] = useState(false);
//-------------------------------------------------------------------------------------------------------------------------------
    const {race, isLoading: isLoadingRace} = useRace();
    const {categories, isLoading: isLoadingCategories} = useCategories();
//-------------------------------------------------------------------------------------------------------------------------------
    const registeredTableId = "registered";
    const {
        sortKeys: skRegistered,
        sortDirections: sdRegistered,
        filters: fRegistered
    } = useTableParams(registeredTableId);

    const urlRegisteredBoats = buildUrl(`/races/${race?.id}/boats`, {
        sortBy: skRegistered,
        sortDirections: sdRegistered,
        filters: fRegistered
    });
    const {
        data: registeredBoatsFiltered,
        mutate: mutateRegisteredBoats,
    } = useSWR<BoatInRace[]>(race ? urlRegisteredBoats : null, getFetcher, {keepPreviousData: true});
//-------------------------------------------------------------------------------------------------------------------------------
    const urlAllRegisteredBoats = `/races/${race?.id}/boats`;
    const {
        data: allRegisteredBoats,
        mutate: mutateAllRegisteredBoats,
    } = useSWR<BoatInRace[]>(race ? urlAllRegisteredBoats : null, getFetcher, {keepPreviousData: true});
//-------------------------------------------------------------------------------------------------------------------------------
    const unregisteredTableId = "unregistered";
    const {
        sortKeys: skUnregistered,
        sortDirections: sdUnregistered,
        setSort: setSortUnregistered,
        page: pageUnregistered,
        rowsPerPage: rppUnregistered,
        filters: fUnregistered,
        setFilter: setFilterUnregistered,
        clearFilters: clearFiltersUnregistered
    } = useTableParams(unregisteredTableId);

    let urlUnregisteredBoats = buildUrl(`/boats`, {
        page: pageUnregistered,
        size: rppUnregistered,
        sortBy: skUnregistered,
        sortDirections: sdUnregistered,
        filters: fUnregistered,
    });
    if (allRegisteredBoats && allRegisteredBoats.length > 0) {
        urlUnregisteredBoats += "&filter:id=!" + allRegisteredBoats.map(boat => boat.boatId).join(",");
    }
    const {
        data: unregisteredBoats,
        mutate: mutateUnregisteredBoats,
    } = useSWR<PaginatedBoats>(urlUnregisteredBoats, getFetcher, {keepPreviousData: true});
//-------------------------------------------------------------------------------------------------------------------------------
    useEffect(() => {
        setSortUnregistered("category", "asc");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (isLoadingRace || !race || isLoadingCategories || !categories) return <Loading/>

    const handleRegisterBoatButtonClick = async (boatId: number, defaultValue: number | null | undefined) => {
        setSelectedId(boatId);
        setDefaultRegisterDialogValue(defaultValue);
        if (race.rules.startNumbers) {
            setRegisterDialogOpen(true);
        } else {
            await createBoatRegistration(boatId, undefined);
        }
    }

    const handleBoatRegistration = async (startNumber: number) => {
        if (!selectedId) return;
        setRegisterDialogOpen(false);
        await createBoatRegistration(selectedId, startNumber);
    }

    const createBoatRegistration = async (boatId: number, startNumber: number | undefined) => {
        postFetcher<BoatRegistrationType, BoatInRace>(`/races/${race.id}/boats`, {
            boatId: boatId,
            startNumber: startNumber
        }).then(() => {
            showNotification("Loď byla úspěšně přihlášena do závodu", "success");
            clearFiltersUnregistered();
            mutateRegisteredBoats();
            mutateAllRegisteredBoats();
        }).catch((error) => {
            if (error.response.status === 409) {
                showNotification("Loď je již registrována do závodu", "error");
            } else if (error.response.status === 400) {
                showNotification("Loď s tímto startovním číslem je již registrována", "error");
            } else {
                showNotification("Přihlášení lodi do závodu se nezdařilo", "error");
                console.error(error);
            }
        });

        setSelectedId(undefined);
        setDefaultRegisterDialogValue(undefined);
    }

    const handleEditRegistrationButtonClick = async (boatId: number, defaultValue: number | null | undefined) => {
        setSelectedId(boatId);
        setDefaultRegisterDialogValue(defaultValue);
        setEditDialogOpen(true);
    }

    const handleEditBoatRegistration = async (startNumber: number) => {
        putFetcher(`/races/${race.id}/boats/${selectedId}`, {
            startNumber
        }).then(() => {
            showNotification("Startovní číslo lodi bylo úspěšně změněno", "success");
            mutateRegisteredBoats();
            mutateAllRegisteredBoats();
        }).catch((error) => {
            if (error.response.status === 409) {
                showNotification("Loď je již registrována do závodu", "error");
            } else if (error.response.status === 400) {
                showNotification("Loď s tímto startovním číslem je již registrována", "error");
            } else {
                showNotification("Změna startovního čísla lodi se nezdařila", "error");
                console.error(error);
            }
        })

        setEditDialogOpen(false);
        setSelectedId(undefined);
        setDefaultRegisterDialogValue(undefined);
    }

    const handleUnregister = async (boatId: number) => {
        confirm({
            description: (
                <Stack>
                    <Typography>Opravdu chcete odhlásit tuto loď ze závodu?</Typography>
                    <Typography>Pokud pro ni byly evidovány průjezdy, tak budou nevratně smazány.</Typography>
                </Stack>
            ),
            title: "Odhlášení lodi ze závodu",
            confirmationText: "Odhlásit",
            cancellationText: "Zrušit",
            confirmationButtonProps: {variant: "contained", color: "error"},
        }).then(async () => {
            try {
                const finishLineRecords = await getFetcher<FinishLineRecord[]>(`/races/${race.id}/finish`);
                const filteredFinishLineRecords = finishLineRecords.filter((record) => record.boat.boatId === boatId);

                for (const record of filteredFinishLineRecords) {
                    await deleteFetcher(`/races/${race.id}/finish/${record.id}`);
                }

                await deleteFetcher(`/races/${race.id}/boats/${boatId}`);
                showNotification("Loď byla úspěšně odhlášena ze závodu", "success");
                mutateRegisteredBoats();
                mutateAllRegisteredBoats();
            } catch (error) {
                showNotification("Chyba při odhlašování lodě nebo průjezdů", "error");
                console.error(error);
            }
        }).catch(() => {
        });
    };

    const handleNewBoatCreated = (newBoat: Boat) => {
        setCreateBoatDialogOpen(false);
        setFilterUnregistered("name", newBoat.name);
        setFilterUnregistered("category", newBoat.category.categoryNumber.toString());
        mutateUnregisteredBoats();
    };

    const unregisteredTable = <EntityTable
        tableId={unregisteredTableId}
        headers={[
            {key: 'name', label: 'Jméno', sortable: true, filtered: true},
            {
                key: 'category',
                label: 'Kategorie',
                sortable: true,
                filtered: true,
                filterConfig: {
                    type: 'multiSelect',
                    options: [
                        ...(categories).map(category => ({
                            label: category.name,
                            value: category.categoryNumber.toString()
                        }))
                    ]
                },
                render: (category) => (
                    <span
                        style={{color: getCategoryColor(category?.categoryNumber), fontWeight: "bold"}}
                    >
                        {category?.name}
                    </span>
                )
            },
            {
                key: "boatNumber",
                label: "Evidenční číslo",
                sortable: true,
                filtered: true,
                filterConfig: {
                    type: 'number',
                    min: 0
                },
                render: (type: string) => type ||
                    <Typography color="textSecondary" fontStyle="italic">Neuvedeno</Typography>
            },
            {
                key: 'controls',
                render:
                    (boat: Boat) => (
                        <Stack direction="row" spacing={1}>
                            <IconButton color="success" title="Přihlásit loď do závodu"
                                        onClick={() => handleRegisterBoatButtonClick(boat.id, boat.raceNumber)}>
                                <AddCircleOutlineIcon/>
                            </IconButton>
                        </Stack>
                    )
            }
        ] as EntityTableHeaderProps[]}
        entities={unregisteredBoats?.content || []}
        paged={true}
        indexes
        entitiesCount={unregisteredBoats?.page.totalElements ?? 0}
    />;

    return (
        <>
            <Stack direction="row" divider={!isSmall ? <Divider orientation="vertical" flexItem/> : undefined}
                   spacing={2} justifyContent="space-between" overflow="auto">
                <Stack direction="column" spacing={0} flexGrow={1}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="h5">
                            Registrované lodě
                        </Typography>
                        {isSmall && (
                            <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                onClick={() => setUnregisteredBoatDialogOpen(true)}
                                title="Registrovat další lodě do závodu"
                            >
                                Registrovat další
                            </Button>
                        )}
                    </Stack>
                    <EntityTable
                        tableId={registeredTableId}
                        headers={[
                            {key: 'name', label: 'Jméno', sortable: true, filtered: true},
                            {
                                key: 'category',
                                label: 'Kategorie',
                                sortable: true,
                                filtered: true,
                                filterConfig: {
                                    type: 'multiSelect',
                                    options: [
                                        ...(categories).map(category => ({
                                            label: category.name,
                                            value: category.categoryNumber.toString()
                                        }))
                                    ]
                                },
                                render: (category: any) => (
                                    <span
                                        style={{color: getCategoryColor(category?.categoryNumber), fontWeight: "bold"}}
                                    >
                                        {category?.name}
                                    </span>
                                )
                            },
                            race.rules.startNumbers ? {
                                key: 'startNumber',
                                label: 'Startovní číslo',
                                sortable: true,
                                filtered: true,
                                filterConfig: {
                                    type: 'number',
                                    min: 0
                                },
                                render: (type: number) => type ||
                                    <Typography color="textSecondary" fontStyle="italic">Neuvedeno</Typography>
                            } : {
                                key: "boatNumber",
                                label: "Evidenční číslo",
                                sortable: true,
                                filtered: true,
                                filterConfig: {
                                    type: 'number',
                                    min: 0
                                },
                                render: (type: string) => type ||
                                    <Typography color="textSecondary" fontStyle="italic">Neuvedeno</Typography>
                            },
                            {
                                key: 'controls',
                                render:
                                    (boat: BoatInRace) => (
                                        <Stack direction="row" spacing={1}>
                                            {race.rules.startNumbers && (
                                                <IconButton color="primary" title="Upravit startovní číslo lodi"
                                                            onClick={() => handleEditRegistrationButtonClick(boat.boatId, boat.startNumber)}>
                                                    <EditIcon/>
                                                </IconButton>
                                            )}
                                            <IconButton color="error" title="Odhlásit loď ze závodu"
                                                        onClick={() => handleUnregister(boat.boatId)}>
                                                <HighlightOffIcon/>
                                            </IconButton>
                                        </Stack>
                                    )
                            }
                        ] as EntityTableHeaderProps[]}
                        entities={registeredBoatsFiltered ?? []}
                        paged={false}
                        indexes
                        entitiesCount={registeredBoatsFiltered?.length ?? 0}
                        showLoading={false}
                    />
                </Stack>
                {!isSmall && (
                    <Stack direction="column" spacing={0} flexGrow={1}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="h5">
                                Nepřihlášené lodě
                            </Typography>
                            <Button
                                variant="outlined"
                                color="success"
                                size="small"
                                onClick={() => setCreateBoatDialogOpen(true)}
                                title="Přidat novou loď, která ještě není v systému (zobrazí se v seznamu níže)"
                            >
                                Přidat novou loď do evidence
                            </Button>
                        </Stack>
                        {unregisteredTable}
                    </Stack>
                )}
            </Stack>
            <BoatRegisterDialog
                open={registerDialogOpen}
                onClose={() => setRegisterDialogOpen(false)}
                onSave={handleBoatRegistration}
                alreadyRegistered={false}
                defaultValue={defaultRegisterDialogValue}
            />
            <BoatRegisterDialog
                open={editDialogOpen}
                onClose={() => setEditDialogOpen(false)}
                onSave={handleEditBoatRegistration}
                alreadyRegistered={true}
                defaultValue={defaultRegisterDialogValue}
            />
            {createBoatDialogOpen && (
                <BoatCreateDialog
                    open={createBoatDialogOpen}
                    onClose={() => setCreateBoatDialogOpen(false)}
                    onSuccess={handleNewBoatCreated}
                />
            )}
            <Dialog
                open={unregisteredBoatDialogOpen}
                onClose={() => setUnregisteredBoatDialogOpen(false)}
                fullScreen={isSmall}
                maxWidth="lg"
            >
                <DialogTitle>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                        <Typography variant="h5" fontWeight="bold">Registrace lodi do závodu</Typography>
                        <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={() => setUnregisteredBoatDialogOpen(false)}
                            startIcon={<HighlightOffIcon/>}
                        >
                            Zavřít
                        </Button>
                    </Stack>
                </DialogTitle>
                <DialogContent dividers>
                    <Stack direction="column" spacing={0} flexGrow={1}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                            <Typography variant="h5">
                                Nepřihlášené lodě
                            </Typography>
                            <Button
                                variant="outlined"
                                color="success"
                                size="small"
                                onClick={() => setCreateBoatDialogOpen(true)}
                                title="Přidat novou loď, která ještě není v systému (zobrazí se v seznamu níže)"
                            >
                                Přidat novou loď do evidence
                            </Button>
                        </Stack>
                        {unregisteredTable}
                    </Stack>
                </DialogContent>
            </Dialog>
        </>
    )
}

export default BoatRegistration;